import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import Modal from 'flarum/common/components/Modal';
import Icon from 'flarum/common/components/Icon';
import humanTime from 'flarum/common/utils/humanTime';
import { countryKey, countryName, flagUrl, languageName, TIMEZONES, PORTAL_TIMEZONE } from '../utils/infoRules';
import { medal } from './Medal';

/**
 * v18 public profile of /u/{username} (what other members, guests and the owner see):
 *   «Профиль»           /u/{username}       ProfileUserPage
 *   «Личная информация» /u/{username}/info  InfoUserPage
 * A flat list of label / value rows; groups are split by thin lines; empty rows and empty
 * groups are not shown (except «Звания» / «Награды»: «В разработке» while there is no data).
 * Everything here comes from the server, which already removed what this viewer may not see
 * (visibility settings) and every unverified club / team (attribute ppk2Public).
 */
const t = (key, params) => app.translator.trans('ppk2-profile.forum.public.' + key, params);
const tr = (key, params) => app.translator.trans('ppk2-profile.forum.' + key, params);
const row = (key, label, value, top) => value === null || value === undefined || value === '' || value === false ? null : m("div", {
  className: 'Ppk2PubRow' + (top ? ' Ppk2PubRow--top' : '') + ' Ppk2PubRow--' + key,
  key: key
}, m("div", {
  className: "Ppk2PubRow-label"
}, label), m("div", {
  className: "Ppk2PubRow-value"
}, value));

/** Non-empty groups of rows, a thin line between them. */
function groups(list) {
  const filled = list.map(g => g.filter(Boolean)).filter(g => g.length);
  const out = [];
  filled.forEach((g, i) => {
    if (i) out.push(m("div", {
      className: "Ppk2PubDivider",
      key: 'd' + i
    }));
    out.push(...g);
  });
  return m("div", {
    className: "Ppk2Pub"
  }, out);
}

/** «25 октября 2026» / «October 25, 2026» / «25. lokakuuta 2026» (locale long date, no «г.» / «р.»). */
const longDate = d => dayjs(d).format('LL').replace(/\s+(г|р)\.$/, '');
const wip = () => m("span", {
  className: "Ppk2PubWip"
}, t('in_development'));
const quoted = name => t('quoted', {
  name
});
function publicData(user) {
  return user.attribute('ppk2Public') || {};
}
class PublicUserPage extends UserPage {
  oninit(vnode) {
    super.oninit(vnode);
    this.loadUser(m.route.param('username'));
  }
}
export class ProfileUserPage extends PublicUserPage {
  content() {
    const user = this.user;
    const p = publicData(user);
    const groupsList = (user.groups() || []).filter(g => g && !g.isHidden());
    const ranks = p.ranks || [];
    const awards = p.awards || [];

    // lastSeenAt comes only when the player allows «Показывать, когда я в сети» (core rule)
    const online = user.lastSeenAt() ? humanTime(user.lastSeenAt()) : null;
    const club = p.club;
    const team = p.team;
    const minis = p.miniTeams || [];
    return groups([[row('mainNick', tr('main.main_nick.label'), p.mainNick ? m("b", null, p.mainNick) : null), row('groups', t('groups'), groupsList.length ? groupsList.map(g => m("span", {
      className: "Ppk2PubGroup",
      style: {
        '--ppk2-group-bg': g.color() || '#667c99'
      }
    }, g.icon() ? m(Icon, {
      name: g.icon()
    }) : null, " ", g.nameSingular())) : null), row('ranks', t('ranks'), ranks.length ? ranks.map(r => m("span", {
      className: 'Ppk2PubRank Ppk2PubRank--' + (r.color === 'green' ? 'green' : 'blue')
    }, r.icon ? m(Icon, {
      name: r.icon
    }) : null, " ", r.title)) : wip()), row('online', t('online'), online), row('joined', t('joined'), user.joinTime() ? longDate(user.joinTime()) : null)], [row('club', tr('club_team.club.label'), club ? m("a", {
      className: "Ppk2PubClub",
      href: club.url,
      target: "_blank",
      rel: "noopener noreferrer nofollow"
    }, club.name) : null), row('clubRole', tr('club_team.role.label'), club && club.role ? tr('club_team.role.roles.' + club.role) : null), row('team', tr('club_team.team.label'), team ? [m("b", null, quoted(team.name)), team.captain ? m("span", {
      className: "Ppk2PubCaptain"
    }, m(Icon, {
      name: "fas fa-star"
    }), " ", tr('club_team.team_role.captain')) : null] : null), row('miniTeams', tr('club_team.mini.label'), minis.length ? minis.map(n => quoted(n)).reduce((acc, x, i) => i ? [...acc, ', ', x] : [x], []) : null)], [row('awards', t('awards'), awards.length ? m("div", {
      className: "Ppk2PubMedals"
    }, awards.map(a => medal(a))) : wip(), !!awards.length)]]);
  }
}
class PhotoModal extends Modal {
  className() {
    return 'Ppk2PhotoModal Modal--large';
  }
  title() {
    return this.attrs.title;
  }
  content() {
    return m("div", {
      className: "Modal-body"
    }, m("img", {
      className: "Ppk2PhotoModal-img",
      src: this.attrs.url,
      alt: ""
    }));
  }
}
const CONTACT_ORDER = ['telegram', 'vk', 'ok', 'max', 'discord', 'facebook', 'whatsapp'];
const CONTACT_ICONS = {
  telegram: 'fab fa-telegram',
  vk: 'fab fa-vk',
  ok: 'fab fa-odnoklassniki',
  discord: 'fab fa-discord',
  facebook: 'fab fa-facebook',
  whatsapp: 'fab fa-whatsapp'
};

/** Link for a stored (already normalized) contact, or null (Discord, MAX). */
function contactUrl(type, value) {
  switch (type) {
    case 'telegram':
      return 'https://t.me/' + value.replace(/^@/, '');
    case 'vk':
    case 'ok':
    case 'facebook':
      return 'https://' + value;
    case 'whatsapp':
      return 'https://wa.me/' + value.replace(/[^0-9]/g, '');
  }
  return null;
}
function timezoneText(tz) {
  const z = TIMEZONES.find(x => x[1] === tz);
  if (!z) return tz || null;
  const off = 'UTC' + (z[2] ? '+' + z[2] : '+0');
  const zone = tr('info.timezone.zones.' + z[0]);
  return t('timezone', {
    zone,
    offset: z[1] === PORTAL_TIMEZONE ? off + ', ' + tr('info.timezone.portal') : off
  });
}
export class InfoUserPage extends PublicUserPage {
  content() {
    const user = this.user;
    const a = k => user.attribute(k);
    const countryRaw = a('ppk2Country');
    const key = countryKey(countryRaw);
    const flag = key ? flagUrl(key) : null;
    const country = countryRaw ? [flag ? m("img", {
      className: "Ppk2PubFlag",
      src: flag,
      alt: ""
    }) : null, key ? countryName(key) : countryRaw] : null;
    let ageLabel = t('age');
    let ageValue = null;
    const age = a('ppk2Age');
    // the owner / admins also receive the date: «Показывать только возраст» is honoured here too
    const birth = a('ppk2ShowAgeOnly') === true ? null : a('ppk2Birthdate');
    if (birth) {
      ageLabel = tr('info.birthdate.label');
      ageValue = longDate(birth) + (age !== null && age !== undefined ? ' (' + tr('info.birthdate.years', {
        count: age
      }) + ')' : '');
    } else if (age !== null && age !== undefined) {
      ageValue = tr('info.birthdate.years', {
        count: age
      });
    }
    const photos = a('ppk2Photos') || [];
    const contacts = a('ppk2Contacts') || {};
    const contactRows = CONTACT_ORDER.filter(type => contacts[type] && contacts[type].value).map(type => {
      const value = contacts[type].value;
      const url = contactUrl(type, value);
      const label = [type === 'max' ? m("span", {
        className: "Ppk2MaxBadge Ppk2PubContactIcon"
      }, "MAX") : m(Icon, {
        name: CONTACT_ICONS[type],
        className: 'Ppk2PubContactIcon Ppk2PubContactIcon--' + type
      }), ' ', tr('contacts.types.' + type)];
      return row('contact-' + type, label, url ? m("a", {
        href: url,
        target: "_blank",
        rel: "noopener noreferrer nofollow"
      }, value) : value);
    });
    return groups([[row('country', tr('info.country.label'), country), row('location', tr('info.location.label'), a('ppk2Location')), row('timezone', tr('info.timezone.label'), a('ppk2Timezone') ? timezoneText(a('ppk2Timezone')) : null), row('language', tr('info.language.label'), a('ppk2Language') ? languageName(a('ppk2Language')) : null), row('age', ageLabel, ageValue), row('bio', tr('info.bio.label'), a('ppk2Bio') ? m("div", {
      className: "Ppk2PubBio"
    }, a('ppk2Bio')) : null, true), row('photos', tr('info.photos.label'), photos.length ? m("div", {
      className: "Ppk2PubPhotos"
    }, photos.map(ph => m("button", {
      type: "button",
      className: "Ppk2PubPhoto",
      onclick: () => app.modal.show(PhotoModal, {
        url: ph.url,
        title: user.displayName()
      })
    }, m("img", {
      src: ph.url,
      alt: "",
      loading: "lazy"
    })))) : null, true)], contactRows]);
  }
}
flarum.reg.add('ppk2-profile', 'forum/components/PublicProfile', { ProfileUserPage: ProfileUserPage,InfoUserPage: InfoUserPage, });